import { AnalyticsBrowser } from '@segment/analytics-next';

import { ENV } from '../../config/env.js';
import type { AnalyticsEventName, IdentifyParams, TrackParams } from './events.js';
import { logger } from '../telemetry/logger.js';
import { buildAnalyticsContext } from './context/buildContext.js';

const analyticsClient = new AnalyticsBrowser();
let initPromise: Promise<void> | null = null;
function isAnalyticsEnabled(): boolean {
  return Boolean(ENV.analytics.segmentWriteKey);
}

/**
 * Delivery failures are intentionally logged instead of returned to callers. This keeps
 * analytics problems visible without making clinical actions depend on Segment.
 */
function ensureInit(): Promise<void> {
  if (!isAnalyticsEnabled()) {
    return Promise.resolve();
  }

  if (initPromise) {
    return initPromise;
  }

  const writeKey = ENV.analytics.segmentWriteKey;
  if (!writeKey) {
    return Promise.resolve();
  }

  logger.info('analytics.init.start', { appEnv: ENV.appEnv });

  initPromise = analyticsClient
    .load({ writeKey })
    .then(() => undefined)
    .then(() => {
      logger.info('analytics.init.success', { appEnv: ENV.appEnv });
    })
    .catch((error: unknown) => {
      logger.warn('analytics.init.failed', {
        errorName: error instanceof Error ? error.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      initPromise = null;
    });

  return initPromise;
}

async function init(): Promise<void> {
  await ensureInit();
}

async function track<TEvent extends AnalyticsEventName>(
  params: TrackParams<TEvent>
): Promise<void> {
  if (!isAnalyticsEnabled()) {
    return;
  }

  await ensureInit();

  if (!isAnalyticsEnabled()) {
    return;
  }

  const properties = 'properties' in params ? params.properties : undefined;
  const context = buildAnalyticsContext(params.context);
  const enrichedProperties = { ...properties, ...context, appEnv: ENV.appEnv };

  try {
    await analyticsClient.track(params.name, enrichedProperties);
  } catch (error: unknown) {
    logger.warn('analytics.track.failed', {
      name: params.name,
      errorName: error instanceof Error ? error.name : 'UnknownError',
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
}

async function identify(params: IdentifyParams): Promise<void> {
  if (!isAnalyticsEnabled()) {
    return;
  }

  await ensureInit();

  if (!isAnalyticsEnabled()) {
    return;
  }

  try {
    await analyticsClient.identify(params.userId, params.traits);
  } catch (error: unknown) {
    logger.warn('analytics.identify.failed', {
      errorName: error instanceof Error ? error.name : 'UnknownError',
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
}

async function reset(): Promise<void> {
  if (!isAnalyticsEnabled()) {
    return;
  }

  if (!initPromise) {
    return;
  }

  try {
    await analyticsClient.reset();
  } catch (error: unknown) {
    logger.warn('analytics.reset.failed', {
      errorName: error instanceof Error ? error.name : 'UnknownError',
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
}

export const analytics = { init, track, identify, reset } as const;
